import React from "react";
import { FaUser, FaMoneyBillWave } from "react-icons/fa";
import { MdArrowBack, MdCheckCircle } from "react-icons/md";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TransactionsApi } from "../../../api/common/apiIndex";
import { toast } from "react-toastify";
import Loader from "../../common/commonUtils/Loader";
import { getApiErrorMessage } from "../../../utils/errorHandling";

const formatMoney = (n, currency) =>
  `${new Intl.NumberFormat("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0))} ${currency}`;

const Line = ({ label, value, right }) => (
  <div className="d-flex justify-content-between align-items-center py-1">
    <div className="text-muted">{label}</div>
    <div className={`fw-semibold ${right ? "text-end" : ""}`}>{value}</div>
  </div>
);

const Section = ({ icon, title, children, variant = "blue" }) => (
  <div className={`ui-option ui-option--${variant} ui-option--stack mb-3`} role="group">
    <div className="ui-option__header">
      <span className="ui-option__icon">{icon}</span>
      <span className="ui-option__title mb-0">{title}</span>
    </div>
    <div className="ui-option__body">
      {children}
    </div>
  </div>
);

const TransferSummary = ({ details, onBack, onConfirm }) => {
  const qc = useQueryClient();
  const { sender, recipient, transfer } = details;

  const sFirst = sender?.firstName ?? sender?.name ?? "";
  const sLast = sender?.lastName ?? "";
  const rFirst = recipient?.firstName ?? recipient?.name ?? "";
  const rLast = recipient?.lastName ?? "";

  const createTransaction = useMutation({
    mutationFn: TransactionsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "transactions",
      });
      qc.invalidateQueries({ queryKey: ["notifications", "latest"] });
      qc.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
    },
  });

  const handleAddTransaction = async () => {
    try {
      const payload = {
        agentId: Number(transfer.agentId),
        sendAmount: Number(transfer.amount),
        sendCurrency: transfer.sendCurrency,
        receiveAmount: Number(transfer.receivedAmount),
        receiveCurrency: transfer.receiveCurrency,
        sender: sender?.id
          ? { id: sender.id }
          : {
              firstName: sFirst,
              lastName: sLast,
              phone: sender?.phone,
              country: sender?.country,
            },
        recipient: recipient?.id
          ? { id: recipient.id }
          : {
              firstName: rFirst,
              lastName: rLast,
              phone: recipient?.phone,
              country: recipient?.country,
            },
      };

      const saved = await createTransaction.mutateAsync(payload);
      onConfirm(saved.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error creating transfer."));
    }
  };

  const loading = createTransaction.isLoading;

  return (
    <section className="ui-panel">
      <header className="ui-panel__header">
        <h3 className="ui-title mb-0">Review &amp; Confirm</h3>
        <div className="ui-subtitle mt-1">
          Double-check the sender, recipient, and transfer amounts before confirming.
        </div>
      </header>

      <Section icon={<FaUser />} title="Sender" variant="blue">
        <Line label="Name" value={`${sFirst} ${sLast}`.trim()} />
        <Line label="Phone" value={sender?.phone || "—"} />
        <Line label="Country" value={sender?.countryName || sender?.country || "—"} />
      </Section>

      <Section icon={<FaUser />} title="Recipient" variant="blue">
        <Line label="Name" value={`${rFirst} ${rLast}`.trim()} />
        <Line label="Phone" value={recipient?.phone || "—"} />
        <Line label="Country" value={recipient?.countryName || recipient?.country || "—"} />
      </Section>

      <Section icon={<FaMoneyBillWave />} title="Transfer" variant="green">
        <Line label="Send Amount" value={formatMoney(transfer.amount, transfer.sendCurrency)} right />
        <Line label="They Receive" value={formatMoney(transfer.receivedAmount, transfer.receiveCurrency)} right />
        <Line
          label="Agent"
          value={
            transfer.agentFirstName && transfer.agentLastName
              ? `${transfer.agentFirstName} ${transfer.agentLastName}`
              : "N/A"
          }
        />
      </Section>

      <div className="ui-actions mt-3 w-100 justify-content-between">
        <button
          type="button"
          className="ui-btn"
          onClick={onBack}
          disabled={loading}
        >
          <MdArrowBack /> Back
        </button>

        <div className="d-flex flex-column align-items-end ms-auto">
          <button
            type="button"
            className="btn btn-primary rounded-pill d-inline-flex align-items-center gap-2"
            onClick={handleAddTransaction}
            disabled={loading}
          >
            {loading ? "Submitting…" : <>Confirm Transfer <MdCheckCircle /></>}
          </button>
          {loading && (
            <Loader
              size="sm"
              text="Adding transaction…"
              className="mt-2"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default TransferSummary;
